/**
 * Hook to preload the user's avatar into the cache
 *
 * This should be used near the app root to ensure the avatar is cached
 * as soon as user data is available, reducing perceived load time.
 */

import { useEffect, useRef } from 'react';
import { useAppSelector } from '../Store/store';
import { GetUser } from '../Features/Auth/userSlice';
import { getGlbUrl } from '../helpers/avatarUrlUtils';
import { preloadAvatarToCache } from '../helpers/avatarCache';
import { preloadAvatar, preloadAnimation } from '../Components/AvatarViewer';
import {
  defaultAvatarClient,
  defaultAvatarProvider,
} from '../Constants/avatarLibrary';
import { USER_ROLES } from '../Constants/roles';
import { useGLTF } from '@react-three/drei';

// Default animation used for avatars
const DEFAULT_ANIMATION = '/idle.fbx';

/**
 * Preload the user's avatar and default animation into both
 * the Cache API (persistent) and Three.js cache (memory)
 */
export function usePreloadUserAvatar(): void {
  const { data: user } = useAppSelector(GetUser);
  const preloadedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return;

    // Determine which avatar to preload - check both avatarUrl and legacy gtRPMUserAvatarUrl
    const userRole = user.gtUserData?.gtUserType;
    const userAvatarUrl = user.avatarUrl || user.gtUserData?.gtRPMUserAvatarUrl;
    const rawAvatarUrl =
      !userAvatarUrl ||
      userAvatarUrl.includes('themirror://avatar/astronaut-male')
        ? userRole === USER_ROLES.PROVIDER
          ? defaultAvatarProvider
          : defaultAvatarClient
        : userAvatarUrl;

    const avatarUrl = getGlbUrl(rawAvatarUrl);

    // Avoid preloading the same avatar twice
    if (preloadedRef.current === avatarUrl) {
      return;
    }

    // Clear the old avatar from Three.js cache to ensure the new one loads
    if (preloadedRef.current) {
      useGLTF.clear(preloadedRef.current);
    }

    preloadedRef.current = avatarUrl;

    console.log('[usePreloadUserAvatar] Preloading avatar:', avatarUrl);

    // Preload avatar model - this caches to both Cache API and Three.js
    preloadAvatar(avatarUrl);

    // Also preload the default animation
    preloadAnimation(DEFAULT_ANIMATION);

    // Additionally, explicitly cache to Cache API for persistence
    preloadAvatarToCache(avatarUrl);
    preloadAvatarToCache(DEFAULT_ANIMATION);
  }, [user]);
}

export default usePreloadUserAvatar;
